/**
 * Map scroll position → Arrow virtual window startIndex per expanded parent.
 */

import { BUILDER_ARROW_ROW_HEIGHT, BUILDER_ARROW_WINDOW_SIZE } from './builderArrowDetailRows';
import {
  getBuilderArrowDimWindow,
  updateBuilderArrowDimStartIndex,
} from './builderArrowDetailWindowStore';

export type ArrowVirtualScrollSegment =
  | { kind: 'fixed'; height: number }
  | { kind: 'virtual-dim'; parentId: number; totalCount: number };

/**
 * Build segments from ordered parent ids (stable scroll mapping).
 */
export function buildArrowVirtualScrollSegmentsForParents(
  parentIdsInOrder: number[],
  expandedParentIds: ReadonlySet<number>,
  includeFooter: boolean,
): ArrowVirtualScrollSegment[] {
  const segments: ArrowVirtualScrollSegment[] = [
    { kind: 'fixed', height: BUILDER_ARROW_ROW_HEIGHT },
  ];

  parentIdsInOrder.forEach((parentId) => {
    segments.push({ kind: 'fixed', height: BUILDER_ARROW_ROW_HEIGHT });
    if (expandedParentIds.has(parentId)) {
      const windowState = getBuilderArrowDimWindow(parentId);
      if (windowState && windowState.totalCount > 0) {
        segments.push({
          kind: 'virtual-dim',
          parentId,
          totalCount: windowState.totalCount,
        });
      }
    }
  });

  if (includeFooter) {
    segments.push({ kind: 'fixed', height: BUILDER_ARROW_ROW_HEIGHT });
  }

  return segments;
}

/**
 * Returns true if any window startIndex changed.
 */
export function syncArrowVirtualWindowsToScrollTop(
  scrollTop: number,
  parentIdsInOrder: number[],
  expandedParentIds: ReadonlySet<number>,
  includeFooter: boolean,
): boolean {
  const segments = buildArrowVirtualScrollSegmentsForParents(
    parentIdsInOrder,
    expandedParentIds,
    includeFooter,
  );

  let cumulativeY = 0;
  let changed = false;
  const viewportAnchor = scrollTop + BUILDER_ARROW_ROW_HEIGHT;

  segments.forEach((segment) => {
    if (segment.kind === 'fixed') {
      cumulativeY += segment.height;
      return;
    }

    const blockTop = cumulativeY;
    const blockHeight = segment.totalCount * BUILDER_ARROW_ROW_HEIGHT;
    cumulativeY += blockHeight;

    const windowState = getBuilderArrowDimWindow(segment.parentId);
    if (!windowState) return;

    const maxStart = Math.max(0, windowState.totalCount - BUILDER_ARROW_WINDOW_SIZE);
    let nextStart = windowState.startIndex;

    if (viewportAnchor <= blockTop) {
      nextStart = 0;
    } else if (viewportAnchor >= cumulativeY) {
      nextStart = maxStart;
    } else {
      const relativeY = viewportAnchor - blockTop;
      nextStart = Math.min(maxStart, Math.max(0, Math.floor(relativeY / BUILDER_ARROW_ROW_HEIGHT)));
    }

    if (nextStart !== windowState.startIndex) {
      updateBuilderArrowDimStartIndex(segment.parentId, nextStart);
      changed = true;
    }
  });

  return changed;
}
